import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Users } from "lucide-react";

interface RelatedItem {
  asin: string;
  title: string;
  brand: string | null;
  image: string | null;
  price_gbp: number | null;
  price_display: string | null;
  rating: number | null;
  detail_url: string;
}

interface AmazonRelatedProductsProps {
  asin?: string | null;
  keywords?: string | null;
  searchIndex?: string;
  productName?: string;
  limit?: number;
}

export function extractAsin(url?: string | null): string | null {
  if (!url) return null;
  const m = url.match(/\/(?:dp|gp\/product)\/([A-Z0-9]{10})(?:[/?]|$)/i);
  return m ? m[1].toUpperCase() : null;
}

export function AmazonRelatedProducts({
  asin,
  keywords,
  searchIndex = "All",
  productName,
  limit = 6,
}: AmazonRelatedProductsProps) {
  const enabled = Boolean(asin || keywords);

  const { data, isLoading } = useQuery({
    queryKey: ["amazon-related", asin, keywords, searchIndex, limit],
    enabled,
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("amazon-related", {
        body: { asin, keywords, searchIndex, limit },
      });
      if (error) throw error;
      return data as { items: RelatedItem[] };
    },
  });

  const items = data?.items || [];
  if (!enabled || isLoading || items.length === 0) return null;

  return (
    <section className="my-10">
      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
        <Users className="h-5 w-5 text-[#16a34a]" />
        Customers also viewed{productName ? ` (similar to ${productName})` : ""}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {items.map((item) => (
          <a
            key={item.asin}
            href={item.detail_url}
            target="_blank"
            rel="sponsored noopener noreferrer nofollow"
            className="group block"
          >
            <Card className="overflow-hidden h-full hover:shadow-md transition border bg-white">
              <div className="aspect-square bg-muted/30 flex items-center justify-center p-2">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform"
                  />
                ) : null}
              </div>
              <CardContent className="p-3">
                {item.brand && (
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">{item.brand}</p>
                )}
                <p className="text-xs font-medium text-foreground line-clamp-2 min-h-[2rem] group-hover:text-[#16a34a]">
                  {item.title}
                </p>
                <div className="mt-1.5 flex items-center justify-between gap-1">
                  {item.price_display ? (
                    <span className="text-sm font-bold text-foreground">{item.price_display}</span>
                  ) : <span />}
                  {item.rating ? (
                    <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                      {Number(item.rating).toFixed(1)}
                    </span>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </a>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">
        As an Amazon Associate we earn from qualifying purchases. Prices/availability sourced from Amazon at the time of viewing.
      </p>
    </section>
  );
}
